<template>
  <div class="deleted-comment" v-if="deleted">
    {{ deletedAlert }}
  </div>
  <div v-else :class="classes" fluid>
    <b-row class="comment-body" :id="'cmt_' + id">
      <div>
        <!-- アイコン -->
        <profile-icon
          class="topic-list-item-icon rounded-circle m-auto"
          :userName="contributor.name"
          :id="contributorId"
          :image="contributorIcon"
          :size="25"
        />
      </div>
      <div class="comment-center">
        <div class="d-flex" style="flex-direction: row">
          <!-- コメント主 -->
          <div
            ref="comment-user"
            class="comment-user"
            v-html="$sanitize(contributor.name, { allowedAttributes: { span: ['class'] } })"
          ></div>
          <!-- ツールチップ(ユーザ名省略時のみ表示) -->
          <b-tooltip
            @show="onUserTooltipShow"
            :target="() => $refs['comment-user']"
            :title="contributor.name"
          />

          <!-- URLコピー＆編集/削除メニュー -->
          <div class="comment-edit">
            <b-iconstack
              v-if="!search"
              :id="'edit-button-comment-' + id"
              class="three-dots"
              font-scale="1"
              tabindex="0"
              ><b-icon-three-dots></b-icon-three-dots
            ></b-iconstack>
            <b-popover
              :target="'edit-button-comment-' + id"
              ref="popover"
              placement="bottom"
              triggers="focus"
            >
              <div>
                <div class="edit-menu-item" v-on:click="onUrlCopyComment">URLコピー</div>
                <div v-if="reactionAvailable" class="edit-menu-item" v-on:click="showFavoriteList">
                  いいね！の一覧を表示する
                </div>
                <div v-if="edit" class="edit-menu-item" v-on:click="onEditComment">編集</div>
                <div v-if="edit" class="edit-menu-item" v-on:click="onDeleteComment">削除</div>
              </div>
            </b-popover>
          </div>
        </div>
        <!-- コメント内容 -->
        <div class="comment-content">
          <div
            v-html="
              $sanitize(contents, {
                allowedAttributes: { span: ['class'], a: ['href', 'name', 'target', 'rel'] },
              })
            "
          />
        </div>
        <div class="comment-info">
          <!-- 時間 -->
          <div class="mr-2">{{ timeStamp }}</div>
          <!-- いいね -->
          <div
            v-if="!search && reactionAvailable"
            style="font-weight: bolder; cursor: pointer"
            v-on:click="onLikeButtonClick"
          >
            {{ likeLabel }}
          </div>
          <div
            v-if="!search && reactionAvailable"
            :class="likeCount ? 'fav-link active' : 'fav-link'"
            v-on:click="showFavoriteList"
          >
            {{ numberOfLike }}
          </div>
        </div>
        <!-- 投稿写真 -->
        <attachment-list :files="photos" :messageId="messageId" :commentId="id" />
      </div>
    </b-row>
    <b-row class="comment-attachement"> </b-row>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, ref, type PropType } from "vue";
import type Vue from "vue";
import { useAppStore } from "@/store";
import "./comment-list-item.scss";
import moment from "moment";
import sanitizeHTML from "../sanitize";
import { useTextProcess } from "@/composables/useTextProcess";
import { useClipboardCopy } from "@/composables/useClipboardCopy";
import { useLegacyRootEvent } from "@/composables/useLegacyRootEvent";
import ProfileIcon from "./ProfileIcon.vue";
import type { Attachment, IconReaction, FavoriteModalUser } from "../model";
import { User, Comment } from "../model";
import VueDefault from "vue";
VueDefault.prototype.$sanitize = sanitizeHTML;

import { /*BootstrapVue, BootstrapVueIcons,*/ BPopover } from "bootstrap-vue";
import { IconReactionType } from "../API";
import { ReactionController } from "../model/reaction-controller";
import AclManager from "../model/acl-manager";
import { allowFeature } from "../direct-app-config";
import AttachmentList from "./attachment/AttachmentList.vue";

// Vue.use(BootstrapVue);
// Vue.use(BootstrapVueIcons);

export type CommentListItemData = Comment;

export default defineComponent({
  name: "comment-list-item",
  components: {
    ProfileIcon,
    AttachmentList,
  },
  props: {
    // コメントID
    id: { type: String, default: () => "", required: true },
    icon: { type: String, default: () => "about:blank" },
    // コメント作成者ID
    user: { type: String, required: true },
    createdAt: { type: Date as PropType<Date>, default: () => moment().toDate() },
    message: { type: String, default: () => "メッセージ", required: true },
    photos: { type: Array as PropType<Attachment[]>, default: () => [] },
    search: { type: String, default: () => "" },
    // 投稿ID
    messageId: { type: String, default: () => "" },
    deleted: { type: Boolean, default: () => false },
    deletedUser: { type: String, default: () => "" },
    // User関連
    users: { type: Array as PropType<User[]>, default: () => [] }, // 組織内ユーザー情報
    viewer: { type: Object as PropType<User>, default: () => User.createNotFoundUser() }, // ログインユーザ情報
    // Reaction
    like: { type: Object as PropType<IconReaction>, default: () => undefined },
  },
  setup(props, { emit }) {
    // 旧 TextProcessMixin / ClipboardCopyMixin → composable（1-D 利用側書き換え）
    const { highLightStr } = useTextProcess();
    const { onUrlCopy } = useClipboardCopy();
    const rootEvent = useLegacyRootEvent();

    // $refs（旧 this.$refs）→ ref()
    const commentUser = ref<HTMLElement | null>(null);
    const popover = ref<Vue | null>(null);

    const classes = computed((): { [selector: string]: boolean } => {
      return {
        "comment-list-item": true,
      };
    });

    const contents = computed((): string => {
      return highLightStr(props.message, props.search);
    });

    const timeStamp = computed((): string => {
      const dt = moment(props.createdAt);
      return dt.format("YYYY/MM/DD HH:mm");
    });

    const likeLabel = computed((): string => {
      return likeFlag.value ? "いいねを取り消す" : "いいね！";
    });

    const numberOfLike = computed((): string => {
      return `${likeCount.value}人`;
    });

    const edit = computed((): boolean => {
      return props.viewer.id == props.user;
    });

    // 作成者情報
    const contributor = computed((): User => {
      const user = props.users.find((u) => u.id == props.user);
      return user || User.createNotFoundUser(props.user);
    });
    const contributorId = computed((): string => contributor.value.directId);
    const contributorIcon = computed((): string => contributor.value.getProfileIconUrl());

    const deletedAlert = computed((): string => {
      const user = findUser(props.deletedUser);
      return `${user.name}により削除されました`;
    });

    /** イイね！のフラグと数 */
    const domainId = computed((): string => useAppStore().domainId);
    const topicId = computed((): string => useAppStore().topicId);
    const likeFlag = computed(
      (): boolean => props.like?.userList.some((u) => u.userId == props.viewer.directId) || false
    );
    const likeCount = computed((): number => props.like?.userList.length || 0);

    // Urlコピー
    const onUrlCopyComment = (): void => {
      (popover.value as unknown as BPopover)?.$emit("close"); // Close
      const dId = useAppStore().domainId;
      const tId = useAppStore().topicId;
      onUrlCopy({ domainId: dId, topicId: tId, messageId: props.messageId, commentId: props.id });
    };

    const findUser = (directUserId: string): User => {
      const user = props.users.find((user) => user.directId == directUserId);
      return user ? user : User.createNotFoundUser(directUserId);
    };

    // イイね！ボタンを押した時の動作
    const onLikeButtonClick = (): void => {
      onReactionClicked(props.id, IconReactionType.LIKE);
    };

    // 権限
    const reactionAvailable = computed((): boolean => {
      if (allowFeature("deny-edit") == false) return true; // 未対応組織なので許可
      return (
        AclManager.getCreateReactionAcl(
          AclManager.store,
          domainId.value,
          topicId.value,
          props.messageId,
          props.id
        ) != "deny"
      );
    });

    const openPhotoStream = (): void => {
      emit("openModal", props.id);
    };

    const onEditComment = (): void => {
      emit("toEditComment", props.id);
    };

    const onDeleteComment = (): void => {
      emit("toDeleteComment", props.id);
    };

    /** Commentのリアクションボタンを押した時の処理 */
    const onReactionClicked = (commentId: string, type: IconReactionType): void => {
      const ctl = new ReactionController();
      const target = ctl.findComment(props.messageId, commentId);
      ctl.onReactionClicked(target, type, props.viewer);
    };

    // いいね一覧表示
    const showFavoriteList = (): void => {
      const userList = props.like?.userList || [];
      const favoriteUsers = userList.map((favoriteUser): FavoriteModalUser => {
        const user = props.users.find((u) => u.directId == favoriteUser.userId);
        return {
          user: user ? user : User.createNotFoundUser(favoriteUser.userId),
          updatedAt: favoriteUser.updatedAt,
        };
      });
      rootEvent.emit("on-favorite-modal", {
        userList: favoriteUsers,
        messageId: props.messageId,
        commentId: props.id,
      });
    };

    // ユーザ名用ツールチップ
    const onUserTooltipShow = (bvEvent: Event): void => {
      const target = commentUser.value;
      if (target instanceof HTMLElement) {
        // webkit-line-clamp で省略された時、offsetHeight < scrollHeigt になる
        const oHeight = target.offsetHeight;
        const sHeight = target.scrollHeight;
        if (oHeight < sHeight) return;
      }
      bvEvent.preventDefault(); // 出さない
    };

    return {
      // $refs（テンプレートの ref 名と一致させる）
      "comment-user": commentUser,
      popover,
      classes,
      contents,
      timeStamp,
      likeLabel,
      numberOfLike,
      edit,
      contributor,
      contributorId,
      contributorIcon,
      deletedAlert,
      domainId,
      topicId,
      likeFlag,
      likeCount,
      onUrlCopyComment,
      onLikeButtonClick,
      reactionAvailable,
      openPhotoStream,
      onEditComment,
      onDeleteComment,
      onReactionClicked,
      showFavoriteList,
      onUserTooltipShow,
    };
  },
});
</script>
